import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  LinearProgress, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'

import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import MerchantSelect from '../AllocationConfig/MerchantSelect'
import { activateForMerchant, errText, listPlans, listSubscriptions, runDailyPlanJob } from './api'
import { LIVE_STATUSES, expiryLabel, inr, statusColor, unlimitedOr } from './constants'
import CancelSubscriptionDialog from './CancelSubscriptionDialog'

/**
 * ProShip Direct — subscriptions.
 *
 * One row per merchant engagement. The usage bar is the number ops actually look at: how much of
 * the allowance is gone, and whether the merchant can still ship on their own couriers.
 *
 * "Activate for merchant" is the offline-payment path — the merchant paid by NEFT/cheque and a
 * KAM switches the plan on, recording the reference. It skips the wallet entirely. For a wallet
 * or credit-line purchase the merchant buys it themselves from their own panel.
 */
export default function Subscriptions() {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [activating, setActivating] = useState(false)
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [cancelling, setCancelling] = useState(null)

  const [runningJob, setRunningJob] = useState(false)
  const runJob = async () => {
    setRunningJob(true)
    try {
      const r = (await runDailyPlanJob())?.data || {}
      enqueueSnackbar(`Checked ${r.checked}: ${r.reminders} reminders, ${r.usageAlerts} allowance alerts, `
        + `${r.renewed} renewed, ${r.renewalFailed} renewals failed, ${r.movedToGrace} to grace, `
        + `${r.suspended} suspended, ${r.expired} expired${r.errors ? `, ${r.errors} errors (see logs)` : ''}`,
      { variant: r.errors ? 'warning' : 'success' })
      load()
    } catch (e) {
      enqueueSnackbar(errText(e), { variant: 'error' })
    } finally {
      setRunningJob(false)
    }
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [s, p] = await Promise.all([listSubscriptions(), listPlans()])
      setRows(Array.isArray(s?.data) ? s.data : [])
      setPlans(Array.isArray(p?.data) ? p.data : [])
    } catch (e) {
      enqueueSnackbar(errText(e), { variant: 'error' })
    } finally { setLoading(false) }
  }, [enqueueSnackbar])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(
    () => (statusFilter === 'ALL' ? rows : rows.filter((r) => r.status === statusFilter)),
    [rows, statusFilter])

  const columns = [
    { field: 'subscriptionNo', headerName: 'Subscription', minWidth: 180, flex: 1 },
    {
      field: 'merchantName', headerName: 'Merchant', minWidth: 200, flex: 1.2,
      valueGetter: ({ row }) => row.merchantName || row.merchantOid || '',
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.merchantName || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{row.merchantOid || row.merchantId}</Typography>
        </Box>
      ),
    },
    { field: 'planCode', headerName: 'Plan', minWidth: 100, flex: 0.6 },
    {
      field: 'status', headerName: 'Status', minWidth: 140, flex: 0.8,
      renderCell: ({ row }) => <Chip size="small" label={row.status} color={statusColor(row.status)} />,
    },
    {
      field: 'usage', headerName: 'Allowance used', minWidth: 190, flex: 1.2, sortable: false,
      renderCell: ({ row }) => <UsageBar usage={row.usage} />,
    },
    {
      field: 'expiresAt', headerName: 'Expires', minWidth: 130, flex: 0.8,
      renderCell: ({ row }) => expiryLabel(row.expiresAt),
    },
    {
      field: 'planFee', headerName: 'Fee', minWidth: 110, flex: 0.7,
      renderCell: ({ row }) => inr(row.planFee),
    },
    {
      field: 'canShip', headerName: 'Can ship', minWidth: 110, flex: 0.6,
      renderCell: ({ row }) =>
        row.usage?.canShip === false
          ? <Tooltip title={row.usage?.blockedReason || ''}><Chip size="small" label="Blocked" color="error" /></Tooltip>
          : <Chip size="small" label="Yes" color="success" />,
    },
    {
      field: 'actions', headerName: 'Actions', minWidth: 190, flex: 1, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={() => navigate(`/direct/subscriptions/${row.id}`)}>
            Details
          </Button>
          {LIVE_STATUSES.includes(row.status) && (
            <Button size="small" variant="outlined" color="error" onClick={() => setCancelling(row)}>
              Cancel
            </Button>
          )}
        </Stack>
      ),
    },
  ]

  return (
    <Page>
      <PageTitle>ProShip Direct — Subscriptions</PageTitle>

      <Stack direction="row" spacing={2} sx={{ mb: 1.5, alignItems: 'center' }}>
        <TextField select size="small" label="Status" value={statusFilter}
                   onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 200 }}>
          {['ALL', 'ACTIVE', 'GRACE', 'PENDING_PAYMENT', 'EXPIRED', 'SUSPENDED', 'CANCELLED']
            .map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        {/*
          The daily job on demand: reminders, renewals and lapses. It also runs every morning on
          the scheduler node; this is for checking a change without waiting. Safe to repeat.
        */}
        <Button variant="outlined" disabled={runningJob} onClick={runJob}>
          {runningJob ? 'Running…' : 'Run plan job now'}
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setActivating(true)}>
          Activate for merchant
        </Button>
      </Stack>

      <ProzoDataGrid
        rows={filtered} columns={columns} loading={loading} paginationMode="client"
        rowCount={filtered.length} page={page} setPage={setPage}
        sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
        getRowId={(r) => r.id} disableColumnMenu
      />

      {activating && (
        <ActivateDialog plans={plans} onClose={() => setActivating(false)}
                        onDone={() => { setActivating(false); load() }}
                        onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
                        onSuccess={(m) => enqueueSnackbar(m, { variant: 'success' })} />
      )}
      {cancelling && (
        <CancelSubscriptionDialog sub={cancelling} onClose={() => setCancelling(null)}
                                  onDone={() => { setCancelling(null); load() }} />
      )}
    </Page>
  )
}

/**
 * Unlimited plans have no bar to draw — showing an empty or full one would both be wrong, so
 * they get the word instead. A negative balance means the merchant is inside their overdraft.
 */
export function UsageBar({ usage }) {
  if (!usage) return <Typography variant="caption" color="text.secondary">—</Typography>
  if (usage.allowance === null || usage.allowance === undefined) {
    return <Chip size="small" label="Unlimited" variant="outlined" />
  }
  const used = usage.used ?? 0
  const pct = usage.allowance > 0 ? Math.min(100, Math.max(0, (used / usage.allowance) * 100)) : 0
  const over = (usage.balance ?? 0) < 0
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
        {used.toLocaleString('en-IN')} / {usage.allowance.toLocaleString('en-IN')}
        {over && <strong> · {Math.abs(usage.balance)} over</strong>}
      </Typography>
      <LinearProgress variant="determinate" value={pct}
                      color={over ? 'error' : pct > 85 ? 'warning' : 'primary'}
                      sx={{ height: 6, borderRadius: 3 }} />
    </Box>
  )
}

function ActivateDialog({ plans, onClose, onDone, onError, onSuccess }) {
  const [merchant, setMerchant] = useState(null)
  const [planId, setPlanId] = useState('')
  const [externalReference, setExternalReference] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!merchant) { onError('Pick a merchant.'); return }
    if (!planId) { onError('Pick a plan.'); return }
    setSaving(true)
    try {
      const res = await activateForMerchant({
        merchantId: merchant.merchantId,
        merchantOid: merchant.id,
        planId,
        externalReference: externalReference || null,
      })
      onSuccess(`Activated ${res?.data?.subscriptionNo || ''} for ${merchant.name}`)
      onDone()
    } catch (e) { onError(errText(e)) } finally { setSaving(false) }
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Activate a plan for a merchant</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Use this when the merchant has <strong>paid offline</strong>. With a payment reference the
          wallet is not touched at all. Leave it blank and the plan fee is debited from their wallet or
          credit line — which fails if they are short, with the shortfall shown.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MerchantSelect value={merchant} onChange={setMerchant} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Plan" value={planId} onChange={(e) => setPlanId(e.target.value)}
                       fullWidth size="small">
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.planCode} — {inr(p.totalPayable)} · {unlimitedOr(p.shipmentsIncluded)} shipments
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Payment reference" value={externalReference}
                       onChange={(e) => setExternalReference(e.target.value)} fullWidth size="small"
                       placeholder="NEFT / cheque / PO number"
                       helperText="Blank = charge the wallet" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Activating…' : 'Activate'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
